// Copyright ©️ 2026 eVolve MEP, LLC

const { Autodesk } = window;

export class PropertiesPanel extends Autodesk.Viewing.UI.DockingPanel {
  declare container: HTMLElement;
  content: HTMLElement;
  viewer: Autodesk.Viewing.GuiViewer3D;
  private titleEl: HTMLElement | null = null;
  private scrollContainerEl: HTMLElement | null = null;

  constructor(
    viewer: Autodesk.Viewing.GuiViewer3D,
    /** Unique ID for the panel. */
    id: string,
    /** Title for the panel. */
    title: string,
    /** Initial content for the panel (will be swapped via setContent). */
    content: HTMLElement,
    options?: Autodesk.Viewing.UI.DockingPanelOptions,
  ) {
    super(viewer.container, id, title, options);
    this.content = content;
    this.viewer = viewer;
    this.createUI();
  }

  initialize() {
    // Override the default initialize method to prevent the base class from adding unnecessary elements.
  }

  /** Align the panel to the top-left corner of the viewer container with the same offset as the toolbar. */
  resetPosition() {
    this.container.style.top = '16px';
    this.container.style.left = '16px';
    this.container.style.width = 'auto';
    this.container.style.height = 'auto';
  }

  createUI() {
    this.container.classList.add('properties-panel');
    this.resetPosition();

    const titleBar = this.createTitleBar(this.titleLabel);
    // Store a reference to the title text element so setContent can update it.
    this.titleEl = titleBar.querySelector('.docking-panel-title') ?? titleBar;
    // Add space for the close button.
    titleBar.style.paddingRight = '42px';
    this.container.appendChild(titleBar);

    const closeButton = this.createCloseButton();
    this.container.appendChild(closeButton);

    // Enable dragging the panel by its title bar.
    this.initializeMoveHandlers(titleBar);

    const scrollContainer = this.createScrollContainer();
    this.scrollContainerEl = scrollContainer;
    scrollContainer.appendChild(this.content);
    this.container.appendChild(scrollContainer);
  }

  /**
   * Swaps the panel title and body to reflect the currently active tool.
   * Must be called before showing the panel so that DOM lookups inside `applyPropertiesToPanel` succeed.
   */
  setContent(title: string, content: HTMLElement) {
    if (this.titleEl) this.titleEl.textContent = title;
    if (this.scrollContainerEl) {
      this.scrollContainerEl.replaceChildren(content);
    }
    this.content = content;
  }
}
